'use client';

import { useEffect, useState } from 'react';
import { useBehaviorTracker } from '@/hooks/useBehaviorTracker';
import { callAiFlow } from '@/lib/ai-client';
import type { RecommendationsOutput } from '@/ai/flows/personalized-recommendations';
import { Sparkles, BrainCircuit, Search, ArrowLeft, Loader2, Bot, Eye, Stethoscope } from 'lucide-react';
import { Button } from './ui/button';
import Link from 'next/link';

const RECOMMENDATION_CACHE_KEY = 'ai_personalized_recommendations_cache_v1';
const RECOMMENDATION_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export function AIPersonalizedRecommendations() {
    const { getHistoryString } = useBehaviorTracker();
    const [recommendation, setRecommendation] = useState<RecommendationsOutput | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function loadRecommendations() {
            try {
                const cachedRaw = localStorage.getItem(RECOMMENDATION_CACHE_KEY);
                if (cachedRaw) {
                    try {
                        const parsed = JSON.parse(cachedRaw) as { expiresAt: number; data: RecommendationsOutput };
                        if (parsed && parsed.expiresAt > Date.now() && parsed.data) {
                            setRecommendation(parsed.data);
                            setLoading(false);
                            return;
                        }
                    } catch {
                        localStorage.removeItem(RECOMMENDATION_CACHE_KEY);
                    }
                }

                const historyData = getHistoryString();
                // Always call it, even if history is empty, to get a general greeting
                const result = await callAiFlow<{ historyString: string }, RecommendationsOutput>(
                    'personalized-recommendations',
                    { historyString: historyData || 'لم يتصفح المستخدم أي شيء بعد. قدم تحية عامة وشجعه على البدء.' }
                );
                setRecommendation(result);
                localStorage.setItem(
                    RECOMMENDATION_CACHE_KEY,
                    JSON.stringify({
                        expiresAt: Date.now() + RECOMMENDATION_CACHE_TTL_MS,
                        data: result,
                    })
                );
            } catch (error) {
                console.error('Failed to load AI recommendations:', error);
            } finally {
                setLoading(false);
            }
        }

        // Small delay to ensure localStorage is hydrated if running immediately on mount
        const timer = setTimeout(() => {
            loadRecommendations();
        }, 500);

        return () => clearTimeout(timer);
    }, []);

    if (loading) {
        return (
            <div className="w-full bg-gradient-to-r from-indigo-50 to-purple-50 rounded-[2rem] p-4 md:p-5 border border-indigo-100/50 shadow-sm mb-6 animate-pulse flex items-center gap-3">
                <div className="bg-indigo-100 p-3 rounded-xl">
                    <Loader2 className="h-5 w-5 text-indigo-500 animate-spin" />
                </div>
                <div>
                    <div className="h-3.5 bg-indigo-200 rounded w-40 mb-2"></div>
                    <div className="h-3 bg-indigo-100 rounded w-64"></div>
                </div>
            </div>
        );
    }

    if (!recommendation) return null;

    return (
        <div className="w-full bg-gradient-to-r from-[#1e1b4b] via-[#312e81] to-[#4338ca] rounded-[2rem] p-5 md:p-6 shadow-2xl shadow-indigo-900/30 relative overflow-hidden group">
            {/* Background Decorators */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500 rounded-full blur-3xl opacity-20 -mr-20 -mt-20 group-hover:opacity-30 transition-opacity duration-1000"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500 rounded-full blur-3xl opacity-20 -ml-10 -mb-10 group-hover:translate-x-10 transition-transform duration-1000"></div>

            <div className="relative z-10 flex flex-col md:flex-row gap-5 md:gap-6 items-center justify-between">
                
                {/* Text Content */}
                <div className="flex-1 w-full space-y-4 text-center md:text-start">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
                        <Sparkles className="h-4 w-4 text-amber-300" />
                        <span className="text-xs font-black text-indigo-50 tracking-wider">مستشارك الذكي</span>
                    </div>

                    <div>
                        <h2 className="text-xl md:text-2xl font-black text-white font-headline leading-snug mb-1">
                            {recommendation.greeting}
                        </h2>
                        <p className="text-indigo-100/90 font-medium leading-relaxed max-w-3xl text-sm">
                            <span className="text-amber-300 font-bold mr-1">نصيحة اليوم:</span>
                            {recommendation.advice}
                        </p>
                    </div>

                    <div className="pt-2 flex justify-center md:justify-start">
                        <Link href={`/buy/results?q=${encodeURIComponent(recommendation.suggestedSearch)}`}>
                            <Button className="bg-white hover:bg-indigo-50 text-indigo-900 rounded-xl h-11 px-5 text-sm font-black shadow-xl shadow-indigo-900/50 gap-2 group/btn transition-transform hover:-translate-y-0.5">
                                <Search className="h-4 w-4 text-indigo-500" />
                                بحث: {recommendation.suggestedSearch}
                                <ArrowLeft className="h-4 w-4 opacity-50 group-hover/btn:-translate-x-1 transition-transform" />
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Icon/Visual */}
                <div className="hidden md:flex flex-col items-center justify-center shrink-0">
                    <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-[1.5rem] border border-white/20 flex items-center justify-center shadow-2xl relative">
                        <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 rounded-[1.5rem] animate-pulse"></div>
                        <BrainCircuit className="h-10 w-10 text-indigo-100 relative z-10" />
                    </div>
                </div>

            </div>
            
            {/* ── AI Quick Actions ── */}
            <div className="w-full mt-6 grid grid-cols-3 gap-3 md:gap-4 relative z-10">
                <Link href="/services/ai-advisor" className="bg-white hover:bg-slate-50 text-slate-800 rounded-2xl p-4 flex flex-col items-center justify-center text-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all hover:-translate-y-1 border border-white/80 group/ai">
                    <div className="w-12 h-12 md:w-14 md:h-14 mb-2 md:mb-3 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 group-hover/ai:scale-110 transition-transform">
                        <Bot className="h-6 w-6 md:h-7 md:w-7" />
                    </div>
                    <span className="font-black text-xs md:text-sm">مستشار السيارة</span>
                </Link>
                <Link href="/inspection" className="bg-white hover:bg-slate-50 text-slate-800 rounded-2xl p-4 flex flex-col items-center justify-center text-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all hover:-translate-y-1 border border-white/80 group/ai">
                    <div className="w-12 h-12 md:w-14 md:h-14 mb-2 md:mb-3 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover/ai:scale-110 transition-transform">
                        <Eye className="h-6 w-6 md:h-7 md:w-7" />
                    </div>
                    <span className="font-black text-xs md:text-sm">فحص بصري</span>
                </Link>
                <Link href="/services/ai-diagnose" className="bg-white hover:bg-slate-50 text-slate-800 rounded-2xl p-4 flex flex-col items-center justify-center text-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all hover:-translate-y-1 border border-white/80 group/ai">
                    <div className="w-12 h-12 md:w-14 md:h-14 mb-2 md:mb-3 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700 group-hover/ai:scale-110 transition-transform">
                        <Stethoscope className="h-6 w-6 md:h-7 md:w-7" />
                    </div>
                    <span className="font-black text-xs md:text-sm">فحص ذكي</span>
                </Link>
            </div>
        </div>
    );
}
